import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const STAGE_LABEL = {
  idle: "JUICE READY",
  sure: "ARE YOU SURE?",
  really: "REALLY SURE?",
  fine: "OKAY FINE, READY",
};

const NEXT_STAGE = {
  idle: "sure",
  sure: "really",
  really: "fine",
};

const RESET_TIMEOUT_MS = 4000;
const DRIP_TOTAL_MS = 4000;
const REDUCED_MS = 1000;

function prefersReducedMotion() {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function generateDrips() {
  const count = 8 + Math.floor(Math.random() * 7);
  const drips = [];
  for (let i = 0; i < count; i += 1) {
    drips.push({
      id: i,
      left: Math.random() * 100,
      width: 12 + Math.random() * 36,
      delay: Math.random() * 600,
      duration: 1600 + Math.random() * 800,
      sway: (Math.random() - 0.5) * 12,
    });
  }
  return drips;
}

function JuiceDripOverlay({ reducedMotion, onComplete }) {
  const dripsRef = useRef(reducedMotion ? [] : generateDrips());

  useEffect(() => {
    const id = setTimeout(onComplete, reducedMotion ? REDUCED_MS : DRIP_TOTAL_MS);
    return () => clearTimeout(id);
  }, [onComplete, reducedMotion]);

  if (typeof document === "undefined") return null;

  if (reducedMotion) {
    return createPortal(
      <div aria-hidden="true" className="juice-overlay-reduced" />,
      document.body
    );
  }

  return createPortal(
    <div aria-hidden="true" className="juice-overlay">
      {dripsRef.current.map((d) => (
        <span
          key={d.id}
          className="juice-drip"
          style={{
            left: `${d.left}%`,
            width: `${d.width}px`,
            animationDelay: `${d.delay}ms`,
            animationDuration: `${d.duration}ms`,
            "--juice-sway": `${d.sway}px`,
          }}
        />
      ))}
      <div className="juice-fade" />
    </div>,
    document.body
  );
}

export default function JuiceButton() {
  const [stage, setStage] = useState("idle");
  const [firing, setFiring] = useState(false);
  const timeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const armReset = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setStage("idle"), RESET_TIMEOUT_MS);
  };

  const handleClick = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (stage === "fine") {
      setStage("idle");
      setFiring(true);
      return;
    }
    const next = NEXT_STAGE[stage];
    if (next) {
      setStage(next);
      armReset();
    }
  };

  const STAGE_STYLE = {
    idle: { bg: "#ffffff", hover: "#fff9ed", text: "#000000" },
    sure: { bg: "#c4f04a", hover: "#d8ff78", text: "#000000" },
    really: { bg: "#ff9500", hover: "#ffb347", text: "#000000" },
    fine: { bg: "#e94b4b", hover: "#ff6b6b", text: "#ffffff" },
  };
  const stageStyle = STAGE_STYLE[stage] || STAGE_STYLE.idle;

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        aria-live="polite"
        data-testid="juice-button"
        className="juice-stage-btn inline-flex items-center justify-center font-mono text-[10px] sm:text-[11px] uppercase tracking-wider border-[2px] border-black rounded-md px-2 py-1 shadow-[2px_2px_0_0_#000] hover:-translate-y-[1px] hover:shadow-[3px_3px_0_0_#000] active:translate-y-[1px] active:shadow-[1px_1px_0_0_#000] font-bold transition-colors shrink-0"
        style={{
          minWidth: "9.5rem",
          backgroundColor: stageStyle.bg,
          color: stageStyle.text,
          "--juice-stage-hover": stageStyle.hover,
        }}
      >
        {STAGE_LABEL[stage]}
      </button>
      {firing && (
        <JuiceDripOverlay
          reducedMotion={prefersReducedMotion()}
          onComplete={() => setFiring(false)}
        />
      )}
    </>
  );
}
